import WRAP from "WRAP-JS";
import {
  configMetarTafDhMetar,
  configMetarTafDhTaf,
  configMetarTafLayerMetar,
  configMetarTafLayerTaf
} from "./config/ConfigMetarTaf";
import { formatUTCDate, parseUTCDate } from "../../../utils/FPEUtils";
import LegendMetarTaf from "../legend/LegendMetarTaf";
import { WX_ID_METARTAF } from "../../../store/WeatherContentsState";

class MetarTaf {
  static NAME = WX_ID_METARTAF;
  static METAR_NAME = 'METAR';
  static TAF_NAME = 'TAF';

  contentsVisibleState = {
    [MetarTaf.METAR_NAME]: true,
    [MetarTaf.TAF_NAME]: true
  };

  baseTime = {
    [MetarTaf.METAR_NAME]: '---',
    [MetarTaf.TAF_NAME]: '---'
  }

  dhMetar;
  dhTaf;
  layerMetar;
  layerTaf;
  ready = false;
  dispatcher;
  visible;

  constructor() {
    this.layerMetar = new WRAP.Geo.Layer(MetarTaf.METAR_NAME);
    this.layerTaf = new WRAP.Geo.Layer(MetarTaf.TAF_NAME);
    this.layerMetar.setVisible(false);
    this.layerTaf.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(allResolve => {

      const metarPromise = new Promise(resolve => {
        this.dhMetar = new WRAP.Data(configMetarTafDhMetar);
        this.dhMetar.updated('data', ref => {
          this._setBaseTime(ref, MetarTaf.METAR_NAME)
          resolve(ref);
        });
      });

      const tafPromise = new Promise(resolve => {
        this.dhTaf = new WRAP.Data(configMetarTafDhTaf);
        this.dhTaf.updated('data', ref => {
          this._setBaseTime(ref, MetarTaf.TAF_NAME)
          resolve(ref);
        });
      });

      // Promise.all([metarPromise, metarAutoPromise, tafPromise]).then(refs => {
      Promise.all([metarPromise, tafPromise]).then(refs => {
        allResolve(refs);
      });
    });
  }

  initLayer() {
    this.layerMetar.configure(this.dhMetar, configMetarTafLayerMetar);
    // this.layerMetarAuto.configure(this.dhMetarAuto, configMetarTafLayerMetarAuto);
    this.layerTaf.configure(this.dhTaf, configMetarTafLayerTaf);

    this.layerMetar.setTooltip((geo, data) => {
      return this._toolTip(geo, data, 'METAR')
    });

    this.layerTaf.setTooltip((geo, data) => {
      return this._toolTip(geo, data, 'TAF');
    });

    WRAP.Geo.addTooltipGroup([this.layerMetar, this.layerTaf]);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, contents) {
    if (!this.ready) return;

    let targetDh;
    let targetLayer;
    switch (contents) {
      case MetarTaf.METAR_NAME:
        targetDh = [this.dhMetar];
        targetLayer = [this.layerMetar];
        break;
      case MetarTaf.TAF_NAME:
        targetDh = [this.dhTaf];
        targetLayer = [this.layerTaf];
        break;
      default:
        targetDh = [this.dhMetar, this.dhTaf];
        targetLayer = this._allLayers()
    }

    if (contents) {
      this.contentsVisibleState[contents] = visible;
    }

    if (visible) {
      targetLayer.forEach(layer => {
        const layerName = layer.name();
        if (this.contentsVisibleState[layerName]) {
          layer.show();
        }
      });
    } else {
      targetLayer.forEach(layer => {layer.hide()});
    }

    targetDh.forEach(dh => {dh.setAutoUpdate('data', visible)});

    if (contents === undefined) {
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  getLegend() {
    return {
      id : MetarTaf.NAME,
      label: 'METAR/TAF',
      component: LegendMetarTaf,
      contents: this
    }
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getTimeInformation() {
    const timeList = [];
    [MetarTaf.METAR_NAME, MetarTaf.TAF_NAME].reduce((list ,t) => {
      const base = this.baseTime[t];
      const time = base !== '---' ? formatUTCDate(parseUTCDate(base), '%Y/%m/%d %H:%MZ') : base;
      list.push(`${t} ${time}`);
      return list;
    }, timeList);

    return {
      id: MetarTaf.NAME,
      label: 'METAR/TAF',
      baseTime: timeList
    };
  }

  changeRoutePointEditing(routePointEditing) {
    this._allLayers().forEach(layer => {
      layer.disableInteraction(routePointEditing);
    });
  }

  _allLayers() {
    return [this.layerMetar, this.layerTaf];
  }

  _setBaseTime(ref, dataType) {
    let icaoList = ref.data;
    if (!icaoList) return;

    const latest = Object.keys(icaoList).reduce((latestTime, icao) => {
      return latestTime < icaoList[icao].announced_date ? icaoList[icao].announced_date : latestTime;
    }, '');

    if (this.baseTime[dataType] !== latest) {
      this.baseTime[dataType] = latest;
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  _toolTip(geo, data, type) {
    const p = geo && geo.properties;
    if (!p || !data) return;
    return `<pre>${type}: ${p.icao}\n ${data.bulletin}</pre>`
  }
}

export default MetarTaf;